import './dealer-locator.scss';
import 'regenerator-runtime/runtime';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { createElementFromHTML, dispatchForCode } from '../../utilities/js/helper';
import Swiper from 'swiper';
import { A11y, Navigation, Pagination, Keyboard } from 'swiper/modules';

class DealerLocator {
    constructor ($map, defaults) {
        this.$mapRoot = $map;
        this.$gmap = this.$mapRoot.querySelector('[data-map="gmap"]');
        this.mapReady = false;
        this.marker = (typeof marker !== 'undefined') ? JSON.parse(marker) : typeof marker; // eslint-disable-line
        this.slider = null;
        this.activeMarker = null;
        this.activeDealerCard = null;
        this.activeDealerMap = null;
        this.latLng = {};
        this.map = {};
        this.mapId = window.googleMapId || 'DEMO_MAP_ID';
        this.mapMarker = [];
        this.markersById = new Map();
        this.mapIcons = mapIcons; // eslint-disable-line
        this.dealers = [];
        this.activeDealers = [];
        this.markerClusterer = null;
        this.$dealersContainer = $map.querySelector('[data-dealer-locator="dealers"]');
        this.$scrollIndicator = $map.querySelector('[data-dealer-locator="indicator"]');
        this.$scrollButton = $map.querySelector('[data-dealer-locator="scrollbutton"]');
        this.$$dealers = [];
        this.$$dealersIdArray = [];
        this.$dealerCards = $map.querySelector('[data-dealer-locator="cards"]');
        this.$dealerCounter = $map.querySelector('[data-dealer-count]');
        this.bounds = null;
        this.myLocationMarker = null;

        this.$autoCompleteInput = $map.querySelector('[data-autocomplete]');
        this.$form = $map.querySelector('[data-dealer-locator="form"]');
        this.$filterSelect = this.$form.querySelector('select[id="dealer-filter"]');
        this.searchLatLng = null;
        this.radius = 30;
        this.clusterRadius = 10;
        this.singleMarkerZoom = 15;
        this.mapBlocked = false;

        this.geolocation = null;
        this.$locateMeButton = $map.querySelector('[data-geolocation]');
        this.$locateMessage = $map.querySelector('[data-dealer-locator="locate-message"]');
        this.autocompleteIsOpen = false;

        if (!defaults) {
            defaults = {
                lat: '51.2257147',
                lng: '7.812349',
                country: 'Deutschland'
            };
        }

        this.mapDefaultProperties = {
            mapId: this.mapId,
            center: new google.maps.LatLng(defaults.lat, defaults.lng), //  eslint-disable-line
            zoom: 6,
            minZoom: 1,
            maxZoom: 20,
            resetZoom: 5,
            zoomIn: 20,
            zoomOut: 4,
            clustering: true,
            panControl: false,
            zoomControl: true,
            zoomControlOptions: {
                position: google.maps.ControlPosition.TOP_RIGHT, //  eslint-disable-line
            },
            scrollwheel: false,
            autocomplete: null,
            autocompleteLastKey: null,
            autocompleteBoundsMarker: null,
            places: null,
            country: defaults.country,
            draggable: true,
            overviewMapControl: false,
            rotateControl: false,
            disableDoubleClickZoom: false,
            disableDefaultUI: true,
            renderingType: google.maps.RenderingType.RASTER, //  eslint-disable-line
        };

        this.mapProperties = Object.assign({}, this.mapDefaultProperties, defaults);
    }

    initialize () {
        this.getInitialDealers();

        this.map = new google.maps.Map(this.$gmap, this.mapProperties); //  eslint-disable-line

        if (this.dealers !== 'undefined') {
            this.dealers.forEach((dealer) => {
                this.addAdvancedMarker(dealer);
            });

            this.addAdvancedCluster();
        }

        if (this.$autoCompleteInput) {
            this.initAutoComplete();
        }

        if (window.innerWidth < 1024) {
            this.initSlider();
        }

        this.setEvents();
        this.checkUrlParams();
    }

    initAutoComplete () {
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    mutation.addedNodes.forEach(node => {
                        if (node.nodeType === 1 && node.classList && node.classList.contains('pac-container')) {
                            this.autocompleteIsOpen = true;
                            this.$autoCompleteInput.classList.add('has--suggestions');
                        }
                    });
                }

                if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                    if (mutation.target.classList.contains('pac-container')) {
                        const style = window.getComputedStyle(mutation.target);
                        if (style.display !== 'none' && !this.autocompleteIsOpen) {
                            this.autocompleteIsOpen = true;
                            this.$autoCompleteInput.classList.add('has--suggestions');
                        } else if (style.display === 'none' && this.autocompleteIsOpen) {
                            this.autocompleteIsOpen = false;
                            this.$autoCompleteInput.classList.remove('has--suggestions');
                        }
                    }
                }
            });
        });

        observer.observe(document.body, { childList: true, subtree: true, attributes: true });

        const setAutoComplete = () => {
            this.mapProperties.autocomplete = new google.maps.places.Autocomplete( //  eslint-disable-line
                /** @type {!HTMLInputElement} */ (
                    this.$autoCompleteInput), {
                    //  types: ['(geocode)'],
                    componentRestrictions: this.mapProperties.countryRestrict
                });

            this.mapProperties.autocomplete.addListener('place_changed', () => {
                this.autoCompletePlace(this.$form);
            });

            this.$form.onsubmit = (e) => {
                e.preventDefault();

                if (this.$autoCompleteInput.value === '' && this.$$dealersIdArray.length > 0) {
                    this.searchLatLng = null;
                    this.$dealerCards.innerHTML = '';
                    this.$$dealersIdArray = [];
                    this.renderDealers();
                }
            };

            this.$autoCompleteInput.addEventListener('keydown', (e) => { //  eslint-disable-line
                if (!document.querySelector('.pac-item-selected')) {
                    dispatchForCode(e, (code) => {
                        if (code === 'Enter' && !e.triggered) {
                            this.selectFirstLocation(true);
                        }
                    });
                }
            });
        };

        setAutoComplete();
    }

    selectFirstLocation (withEnter) {
        let evt;

        if (document.documentElement.classList.contains('no-csshover')) {
            const Event1 = new Event('keydown');

            Event1.code = 'ArrowDown';
            Event1.key = 'ArrowDown';
            Event1.keyCode = 40;
            google.maps.event.trigger(this.$autoCompleteInput, 'keydown', Event1); //  eslint-disable-line

            if (withEnter) {
                const Event2 = new Event('keydown');

                Event2.code = 'Enter';
                Event2.key = 'Enter';
                Event2.keyCode = 13;
                Event2.triggered = true;
                google.maps.event.trigger(this.$autoCompleteInput, 'keydown', Event2); //  eslint-disable-line
            }
        } else {
            evt = new KeyboardEvent('keydown', { keyCode: 40, which: 40, code: 'ArrowDown' });
            this.$autoCompleteInput.dispatchEvent(evt);

            if (withEnter) {
                this.$autoCompleteInput.dispatchEvent(new KeyboardEvent('keydown', { keyCode: 13, which: 13, code: 'Enter' }));
            }
        }
    }

    autoCompletePlace () {
        const result = this.mapProperties.autocomplete.getPlace();

        if (typeof result.geometry === 'undefined') {
            let autocompleteService = new google.maps.places.AutocompleteService(); //  eslint-disable-line
            autocompleteService.getPlacePredictions({
                input: result.name,
                offset: result.name.length
            });
        } else {
            this.searchLatLng = new google.maps.LatLng(result.geometry.location.lat(), result.geometry.location.lng()); //  eslint-disable-line

            this.getFinalZipCode(result).then((zipCode) => {
                this.renderDealers(true, zipCode, () => {
                    this.$dealersContainer.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    });

                    const zoom = this.getZoomByMeters((this.radius * 1000), this.map.getCenter().lat());

                    this.map.setZoom(Math.floor(zoom) - 1); // eslint-disable-line
                    this.map.panTo(this.searchLatLng); // eslint-disable-line
                    this.setMyLocationMarker(result.geometry.location.lat(), result.geometry.location.lng(), true);
                });
            });
        }
    }

    getZoomByMeters (meters, lat) {
        let mapWidth;
        const pixelRatio = window.devicePixelRatio;
        const $mapWrapper = this.$gmap.closest('.dealer-locator__map-container');

        if ($mapWrapper.clientHeight <= $mapWrapper.clientWidth) {
            mapWidth = ($mapWrapper.clientHeight / pixelRatio);
        } else {
            mapWidth = ($mapWrapper.clientWidth / pixelRatio);
        }

        const latitudinalAdjustment = Math.cos(Math.PI * lat / 180.0);
        const arg = 40075004 * mapWidth * latitudinalAdjustment / (meters * 256.0);

        return (Math.log(arg) / Math.log(2.0));
    }

    async getFinalZipCode (result) {
        let zipCode = this.findZipCodeInResult(result.address_components);

        if (zipCode === null) {
            zipCode = await this.getZipCodeForCoords(
                result.geometry.location.lat(),
                result.geometry.location.lng()
            );
        }

        return zipCode;
    }

    addAdvancedCluster () {
        const encodedClusterSVG = encodeURIComponent(`<svg width="33" height="40" viewBox="0 0 33 40" xmlns="http://www.w3.org/2000/svg"><path d="M16.4998 2.00034C24.5857 2.00034 31.1634 8.57528 31.1667 16.6605L31.1599 17.2064C30.9925 22.8265 27.8817 27.7611 24.8699 31.2708C21.7366 34.9221 18.5366 37.2338 18.2371 37.4456L18.2361 37.4447C17.7289 37.8052 17.1221 37.9994 16.4998 37.9994C15.8773 37.9993 15.2706 37.8054 14.7634 37.4447V37.4456C14.4638 37.2338 11.2639 34.9221 8.13062 31.2699C5.02164 27.6458 1.80754 22.5027 1.83374 16.6527C1.83681 8.57517 8.41436 2.00059 16.4998 2.00034Z" fill="#292929" stroke="white" stroke-width="2.66667"/></svg>`) //  eslint-disable-line
            .replace(/'/g, '%27')
            .replace(/"/g, '%22');
        const svgDataURI = `data:image/svg+xml;charset=utf-8,${encodedClusterSVG}`;

        this.markerClusterer = new MarkerClusterer({
            map: this.map,
            markers: this.mapMarker,
            algorithm: new SuperClusterAlgorithm({ radius: this.clusterRadius }), // Radius in Pixeln
            renderer: {
                render: ({ count, position }) => {
                    const clusterIconWrapper = document.createElement('div');
                    clusterIconWrapper.className = 'gmap-cluster__wrapper';
                    clusterIconWrapper.style.backgroundImage = `url('${svgDataURI}')`;

                    const clusterCountElement = document.createElement('span');
                    clusterCountElement.className = 'gmap-cluster__count';
                    clusterCountElement.textContent = String(count);
                    clusterIconWrapper.appendChild(clusterCountElement);

                    return new google.maps.marker.AdvancedMarkerElement({//  eslint-disable-line
                        position,
                        content: clusterIconWrapper
                    });
                }
            }
        });
    }

    generateLocationIcon (type = 'location') {
        const locationIcon = document.createElement('img');
        locationIcon.src = this.mapIcons[type];
        locationIcon.alt = 'Location Icon';
        locationIcon.style.width = '24px';
        locationIcon.style.height = '24px';
        locationIcon.style.border = 'none';

        return locationIcon;
    }

    getInitialDealers (mapBlocked) {
        if (typeof dealers !== 'undefined') { //  eslint-disable-line
            this.dealers = dealers; //  eslint-disable-line
            this.$dealerCounter.textContent = this.dealers.length;

            if (this.dealers.length <= 1 || this.activeDealers.length > 0) {
                this.mapProperties.clustering = false;
            }

            if (mapBlocked) {
                this.mapBlocked = true;
            }

            this.renderDealers();
        } else {
            console.warn('no Dealers where found!!');
        }
    }

    disableForm () {
        this.$form.setAttribute('inert', '');
    }

    fitMapToMarkers () {
        this.bounds = new google.maps.LatLngBounds(); // eslint-disable-line

        this.activeDealers.forEach(marker => {
            this.bounds.extend(marker.position);
        });

        this.map.fitBounds(this.bounds);
    }

    renderDealers (distance, zipCode, callback) {
        const activeFilter = this.$filterSelect.value;
        const distanceArray = [];
        const doubleBrandStoreArray = [];

        const setHTML = (distance) => {
            if (distance) {
                this.$dealerCards.innerHTML = '';
            }

            this.dealers.forEach((dealer, index) => {
                dealer.meters = '';

                if (distance && this.searchLatLng !== null) {
                    const targetLatLng = new google.maps.LatLng(dealer.position.lat, dealer.position.lng);//  eslint-disable-line
                    dealer.meters = google.maps.geometry.spherical.computeDistanceBetween(this.searchLatLng, targetLatLng);//  eslint-disable-line
                }

                let extraClass = '';
                if (activeFilter !== 'all') {
                    if ((activeFilter === 'double' && dealer.doublebrandstore === false) || (activeFilter === 'default' && dealer.doublebrandstore === true)) {
                        extraClass = 'dealer-teaser--hidden';
                    }
                }

                let CardHtmlString = `<div class="dealer-teaser swiper-slide ${extraClass}" data-dealer-locator="dealer" data-dealer-type="${dealer.doublebrandstore ? 'double' : 'default'}" data-dealer-id="${dealer.id}">`;

                if (dealer.doublebrandstore !== false) {
                    CardHtmlString += `<span class="dealer-teaser__doublebrand icon-dbs_inverted">${window.translations.double_brand}</span>`; // eslint-disable-line
                }

                CardHtmlString += `<h3 class="dealer-teaser__headline">${dealer.name}</h3>`;

                if (dealer.meters !== '') {
                    CardHtmlString += `<span class="dealer-teaser__distance">${(dealer.meters / 1000).toFixed(1)}${window.translations.distance_unit}</span>`; // eslint-disable-line
                }

                CardHtmlString += `<div class="dealer-teaser_content">`; // eslint-disable-line
                CardHtmlString += `<address class="dealer-teaser__address icon-location">`;//  eslint-disable-line
                CardHtmlString += `<p>${dealer.address.street}<i>|</i>${dealer.address.city}</p>`;
                CardHtmlString += `</address>`;//  eslint-disable-line

                if (dealer.contact !== '') {
                    if (dealer.contact.phone !== '') {
                        CardHtmlString += `<div><a href="tel:${dealer.contact.phone}" class="dealer-teaser__link icon-phone">${dealer.contact.phone}</a></div>`;
                    }
                    if (dealer.contact.web !== '') {
                        CardHtmlString += `<div><a href="${dealer.contact.web}" target="_blank" class="dealer-teaser__link icon-external_link">${window.translations.visit_website}</a></div>`;
                    }
                    if (dealer.contact.brandStore !== null) {
                        CardHtmlString += `<div><a href="${dealer.contact.brandStore}" target="_blank" class="dealer-teaser__link icon-dbs">${window.translations.double_brand_visit}</a></div>`;
                    }
                }
                CardHtmlString += `<p class="dealer-teaser__link-wrap"><a href="https://www.google.com/maps/dir/?api=1&destination=${dealer.position.lat},${dealer.position.lng}" class="textlink dealer-teaser__route icon-arrow_maximal_right" rel="noopener noreferrer" target="_blank">${mapIcon}${window.translations.calculate_route}</a></p>`; // eslint-disable-line
                CardHtmlString += '</div>';

                const $dealerCard = createElementFromHTML(CardHtmlString);

                if (!this.mapBlocked) {
                    $dealerCard.addEventListener('click', (event) => {
                        if (event.target.tagName !== 'A') {
                            this.setNewCardActive($dealerCard);
                            this.activeDealerCard = $dealerCard;
                            this.dealerSync($dealerCard.dataset.dealerId, 'card');
                        }
                    });
                }

                if (distance) {
                    distanceArray[dealer.meters + index] = $dealerCard;
                    this.$$dealersIdArray[dealer.id] = {
                        meters: dealer.meters
                    };
                } else {
                    this.$dealerCards.appendChild($dealerCard);
                }
            });

            if (distance) {
                const entries = Object.entries(distanceArray).map(([indexStr, value]) => ({
                    index: parseInt(indexStr, 10),
                    value: value
                }));

                entries.sort((a, b) => {
                    return a.index - b.index;
                });

                entries.forEach(($sortedCard) => {
                    const meter = parseInt(this.$$dealersIdArray[parseInt($sortedCard.value.dataset.dealerId)].meters, 10) / 1000;

                    if (meter <= 100 && $sortedCard.value.dataset.dealerType === 'double') {
                        doubleBrandStoreArray.push($sortedCard.value);
                    } else {
                        this.$dealerCards.appendChild($sortedCard.value);
                    }
                });

                if (doubleBrandStoreArray.length > 0) {
                    doubleBrandStoreArray.reverse();

                    for (const $doubleDealer of doubleBrandStoreArray) {
                        this.$dealerCards.prepend($doubleDealer);
                    }
                }
            }

            this.$$dealers = this.$dealerCards.querySelectorAll('[data-dealer-locator="dealer"]');

            if (typeof callback === 'function') {
                callback();
            }
        };

        if (distance) {
            setHTML(distance);
        } else {
            setHTML();
        }
    }

    filterDealers (filter) {
        let counter = 0;
        this.activeDealers = [];

        this.$dealersContainer.querySelectorAll('[data-dealer-locator="dealer"]').forEach(($dealer) => {
            const type = $dealer.dataset.dealerType;
            const mapMarker = this.markersById.get(parseInt($dealer.dataset.dealerId));

            if (filter === 'all') {
                counter++;
                $dealer.classList.remove('dealer-teaser--hidden');
                mapMarker.map = this.map;
                this.activeDealers.push(mapMarker);
            } else {
                if (type === filter) {
                    counter++;
                    $dealer.classList.remove('dealer-teaser--hidden');
                    mapMarker.map = this.map;
                    this.activeDealers.push(mapMarker);
                } else {
                    $dealer.classList.add('dealer-teaser--hidden');
                    mapMarker.map = null;
                }
            }
        });

        if (this.myLocationMarker !== null) {
            this.activeDealers.push(this.myLocationMarker);
        }

        this.$dealerCounter.textContent = counter.toString();
        this.markerClusterer.clearMarkers();
        this.markerClusterer.addMarkers(this.activeDealers);
        this.fitMapToMarkers();
    }

    findZipCodeInResult (addressComponents) {
        for (const component of addressComponents) {
            if (component.types.includes('postal_code') && component.long_name.length >= 4) {
                return component.long_name;
            }
        }
        return null; // No zipCode / postal_code found
    }

    checkUrlParams () {
        const urlParams = new URLSearchParams(window.location.search);
        const zipcode = urlParams.get('zipcode');

        if (zipcode && this.$autoCompleteInput) {
            this.$autoCompleteInput.value = zipcode;

            setTimeout(() => {
                this.$autoCompleteInput.focus();
                this.$autoCompleteInput.dispatchEvent(new Event('input', { bubbles: true }));
                this.$autoCompleteInput.focus();
            }, 100);

            if (this.autocompleteIsOpen) {
                this.selectFirstLocation(true);
            } else {
                const checkInterval = setInterval(() => {
                    if (this.autocompleteIsOpen || this.$autoCompleteInput.classList.contains('has--suggestions')) {
                        clearInterval(checkInterval);
                        this.selectFirstLocation(true);
                    }
                }, 100);

                setTimeout(() => clearInterval(checkInterval), 4000);
            }
        }
    }

    async getZipCodeForCoords (lat, lng) {
        const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${window.googleMapsApiGeocodeKey}`;

        try {
            const response = await fetch(url);

            if (!response.ok) {
                console.error(`HTTP-Fehler! Status: ${response.status}`);
                return null;
            }

            const data = await response.json();

            if (data.status === 'OK' && data.results.length > 0) {
                const addressComponents = data.results[0].address_components;
                const postalCode = this.findZipCodeInResult(addressComponents);

                if (postalCode) {
                    return postalCode;
                } else {
                    return null;
                }
            } else {
                return null;
            }
        } catch (error) {
            return null;
        }
    }

    async addAdvancedMarker (data) {
        if (typeof data.position.lat !== 'undefined') {
            const iconType = data.doublebrandstore ? 'double' : 'location';
            let currentMarker = new google.maps.marker.AdvancedMarkerElement({ //  eslint-disable-line
                position: new google.maps.LatLng(data.position.lat, data.position.lng), //  eslint-disable-line
                id: data.id,
                map: this.map,
                content: this.generateLocationIcon(iconType)
            });

            this.mapMarker.push(currentMarker);
            this.markersById.set(data.id, currentMarker);

            currentMarker.addListener('click', () => {
                this.activeDealerMap = data.id;
                this.setActiveMarker(data.id, currentMarker, iconType);
                this.dealerSync(data.id, 'map');
            });
        } else {
            console.warn('Latitude undefined');
        }
    }

    setActiveMarker (id, marker, iconType) {
        this.checkForActive(iconType);
        marker.content.src = this.mapIcons[iconType + 'NoFocus'];

        marker.content.style.height = '40px';
        marker.content.style.width = '40px';
        this.map.panTo(marker.position);
        this.map.setZoom(this.singleMarkerZoom);
        this.activeMarker = marker;
        this.activeMarker.iconType = iconType;
    }

    //  REACTON EVENT WHO WAS FIRED
    setEvents () {
        google.maps.event.addListenerOnce(this.map, 'idle', () => { //  eslint-disable-line
            this.mapReady = true;
        });

        if (this.dealers !== 'undefined') {
            this.map.addListener('click', () => {
                this.checkForActive();
            });
        }

        this.$locateMeButton.addEventListener('click', () => {
            this.getGeoLocation((location) => {
                if (location !== null) {
                    if (location.lat && location.lng) {
                        this.searchLatLng = new google.maps.LatLng(location.lat, location.lng);//  eslint-disable-line
                        this.$autoCompleteInput.value = window.translations.released_own_position;
                        this.getZipCodeForCoords(location.lat, location.lng).then((zipCode) => {
                            this.renderDealers(true, zipCode, () => {
                                this.$dealersContainer.scrollTo({
                                    top: 0,
                                    behavior: 'smooth'
                                });

                                const zoom = this.getZoomByMeters((this.radius * 1000), this.map.getCenter().lat());

                                this.map.setZoom(Math.floor(zoom) - 1); // eslint-disable-line
                                this.map.panTo(this.searchLatLng); // eslint-disable-line
                                this.setMyLocationMarker(location.lat, location.lng, true);
                            });
                        });
                    }
                }
            });
        });

        window.mediaMatchHandler[1024].addEventListener('change', (match) => {
            if (!match.matches) {
                this.initSlider();
            }
            if (match.matches) {
                this.destroySlider();
            }
        });

        if (this.$filterSelect) {
            this.$filterSelect.addEventListener('change', () => {
                const value = this.$filterSelect.value;
                this.filterDealers(value);
            });
        }

        this.setStickyElement();
    }

    setMyLocationMarker (lat, lng, active) {
        if (this.myLocationMarker !== null) {
            this.myLocationMarker.map = (active === true) ? this.map : null;
            this.myLocationMarker.position = new google.maps.LatLng(lat, lng); // eslint-disable-line
        } else {
            this.myLocationMarker = new google.maps.marker.AdvancedMarkerElement({ // eslint-disable-line
                position: new google.maps.LatLng(lat, lng), //  eslint-disable-line
                map: (active === true) ? this.map : null,
                content: this.generateLocationIcon('myLocation')
            });

            this.myLocationMarker.addListener('click', () => {
                this.map.panTo(this.myLocationMarker.position);
                this.map.setZoom(this.singleMarkerZoom);
            });
        }

        if (active) {
            this.myLocationMarker.content.style.height = '32px';
            this.myLocationMarker.content.style.width = '32px';
            this.activeDealers.push(this.myLocationMarker);
        }
    }

    setNewCardActive ($dealer) {
        const $lastActive = this.$dealersContainer.querySelector('[data-dealer-locator="dealer"].dealer-teaser--selected');

        if ($lastActive) {
            $lastActive.classList.remove('dealer-teaser--selected');
        }

        $dealer.classList.add('dealer-teaser--selected');
    }

    getGeoLocation (callback) {
        this.$locateMessage.innerHTML = '';
        let inertTimeout;

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                this.geolocation = { lat: parseFloat(position.coords.latitude), lng: parseFloat(position.coords.longitude) };

                if (typeof callback === 'function') {
                    this.$locateMessage.textContent = window.translations.geolocation_success;
                    this.$locateMessage.removeAttribute('inert');

                    inertTimeout = setTimeout(() => {
                        clearTimeout(inertTimeout);
                        this.$locateMessage.setAttribute('inert', '');
                    }, 5000);

                    const data = { lat: position.coords.latitude, lng: position.coords.longitude };
                    callback(data);
                }
            }, (error) => {
                let errorMessage;
                switch (error.code) {
                case error.PERMISSION_DENIED:
                    errorMessage = window.translations.permission_denied;
                    break;
                case error.POSITION_UNAVAILABLE:
                    errorMessage = window.translations.position_unavailable;
                    break;
                case error.TIMEOUT:
                    errorMessage = window.translations.timeout;
                    break;
                case error.UNKNOWN_ERROR:
                    errorMessage = window.translations.unknown_error;
                    break;
                default:
                    errorMessage = window.translations.geolocation_error;
                }

                this.$locateMessage.textContent = errorMessage;
                this.$locateMessage.removeAttribute('inert');

                inertTimeout = setTimeout(() => {
                    clearTimeout(inertTimeout);
                    this.$locateMessage.setAttribute('inert', '');
                }, 5000);
            });
        } else {
            this.$locateMessage.textContent = window.translations.geolocation_not_supported;
            this.$locateMessage.removeAttribute('inert');

            inertTimeout = setTimeout(() => {
                clearTimeout(inertTimeout);
                this.$locateMessage.setAttribute('inert', '');
            }, 5000);

            if (typeof callback === 'function') {
                callback(null);
            }
        }
    }

    dealerSync (dealerId, type) {
        dealerId = parseInt(dealerId);

        if (typeof dealerId !== 'undefined') {
            if (type === 'card') {
                const markerToClick = this.markersById.get(dealerId);
                const iconType = (this.$dealersContainer.querySelector(`[data-dealer-id="${dealerId}"]`).dataset.dealerType === 'default') ? 'location' : 'double';

                if (markerToClick) {
                    this.setActiveMarker(dealerId, markerToClick, iconType);
                    this.map.panTo(markerToClick.position);
                    this.map.setZoom(this.singleMarkerZoom);
                }
            } else if (type === 'map') {
                const $cardToClick = this.$dealersContainer.querySelector(`[data-dealer-id="${dealerId}"]`);

                if ($cardToClick) {
                    this.setNewCardActive($cardToClick);

                    if (window.innerWidth >= 1024) {
                        $cardToClick.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start'
                        });
                    } else {
                        const slideIndex = Array.from(this.slider.slides).findIndex((slide) => {
                            return parseInt(slide.dataset.dealerId) === dealerId;
                        });

                        if (slideIndex >= 0) {
                            const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--siteheader-height').trim());

                            window.scrollTo({
                                top: this.$dealersContainer.getBoundingClientRect().top - headerHeight,
                                behavior: 'smooth'
                            });

                            this.slider.slideTo(slideIndex);
                        }
                    }
                }
                this.activeDealerCard = $cardToClick;
            }
        } else {
            console.warn('No dealerId');
        }
    }

    checkForActive (iconType) {
        if (this.activeMarker !== null) {
            if (iconType === undefined) {
                iconType = this.activeMarker.iconType;
            }

            this.activeMarker.content.style.height = '24px';
            this.activeMarker.content.style.width = '24px';
            this.activeMarker.content.src = this.mapIcons[iconType];
            this.activeMarker = null;
        }

        if (this.activeDealerCard !== null) {
            this.activeDealerCard.classList.add('dealer-teaser--selected');
            this.activeDealerCard = null;
        }
    }

    setStickyElement () {
        this.$dealersContainer.addEventListener('scroll', () => {
            const atBottom = this.$dealersContainer.scrollTop + this.$dealersContainer.clientHeight >= this.$dealersContainer.scrollHeight - 2;
            this.$scrollIndicator.classList.toggle('is--hidden', atBottom);
        });

        this.$scrollButton.addEventListener('click', () => {
            const containerRect = this.$dealersContainer.getBoundingClientRect();

            const nextCard = Array.from(this.$$dealers).find((card) => {
                const rect = card.getBoundingClientRect();
                const topOffset = rect.top - containerRect.top;
                return topOffset >= 1;
            });

            if (nextCard) {
                const offsetTop = nextCard.offsetTop;
                const cardHeight = nextCard.offsetHeight;

                this.$dealersContainer.scrollTo({
                    top: offsetTop + cardHeight,
                    behavior: 'smooth'
                });
            }
        });
    }

    initSlider () {
        this.slider = new Swiper(this.$dealersContainer, {
            modules: [A11y, Navigation, Pagination, Keyboard],
            a11y: true,
            keyboard: true,
            slidesPerView: 'auto',
            resistanceRatio: 0,
            spaceBetween: 24,
            autoHeight: false,
            pagination: {
                el: this.$dealersContainer.querySelector('[data-dealer-locator-slider="fraction"]'),
                type: 'fraction'
            },
            navigation: {
                prevEl: this.$dealersContainer.querySelector('[data-dealer-locator-slider="prev"]'),
                nextEl: this.$dealersContainer.querySelector('[data-dealer-locator-slider="next"]')
            }
        });
    }

    destroySlider () {
        if (this.slider) {
            this.slider.destroy(true, true);
        }
    }
}

export { DealerLocator };

window.addEventListener('googleApiLoaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $map = $context.querySelector('[data-map="root"]');

        if ($map) {
            const $gmapContainer = $context.querySelector('[data-map="gmap"]');

            if ($gmapContainer) {
                const $googlemap = new DealerLocator($map, {
                    lat: parseFloat($gmapContainer.dataset.lat),
                    lng: parseFloat($gmapContainer.dataset.lng),
                    country: $gmapContainer.dataset.country
                });
                $googlemap.initialize();
            }
        }
    }
});
